import React, { useCallback, useEffect, useState } from "react";

import { Button, Checkbox, Col, DatePicker, Divider, Drawer, Flex, Form, Input, Row, Select } from "antd";

import { useDispatch, useSelector } from "react-redux";

import { useTranslation } from "react-i18next";

import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import slice from "./reducer";
import saga from "./saga";

import { makeSelectOpen, makeSelectLoading, makeSelectContractor } from "./selectors";

import ContractorModel, { ContractorDataConverter } from "../../viewModels/Contractors/ContractorModel";
import getContractorTypeLabel from "../../utils/getContractorTypeLabel";
import {
  makeSelectCountries,
  makeSelectCurrencies,
  makeSelectLanguage,
  makeSelectUserInfo,
  makeSelectUsers,
} from "../../containers/App/selectors";
import getLocalizedName from "../../utils/getLocalizedName";
import { ContractorRequisiteTemplate } from "../../dto/Contractors/ContractorDto";
import getContactTypeLabel from "../../utils/getContactTypeLabel";
import useInjectSaga from "../../utils/useInjectSaga";

type EditContractorFormInputModel = ContractorModel;

function EditContractorDrawer() {
  const { t } = useTranslation("translations");

  useInjectSaga("editContractorDrawer", saga);

  const [title, setTitle] = useState<"contractor_adding" | "contractor_editing">("contractor_adding");

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const isDrawerOpen = useSelector(makeSelectOpen);
  const isDrawerLoading = useSelector(makeSelectLoading);
  const contractorToEdit = useSelector(makeSelectContractor);

  const lang = useSelector(makeSelectLanguage());
  const countries = useSelector(makeSelectCountries());
  const currencies = useSelector(makeSelectCurrencies());
  const users = useSelector(makeSelectUsers());

  const russia = countries.find((x) => x.name === "Россия")!;
  const china = countries.find((x) => x.name === "Китай")!;
  const kazahstan = countries.find((x) => x.name === "Казахстан")!;
  const belarus = countries.find((x) => x.name === "Беларусь")!;

  const template: ContractorRequisiteTemplate = Form.useWatch(["requisites", "template"], form);

  const userInfo = useSelector(makeSelectUserInfo());

  useEffect(() => {
    if (isDrawerOpen) {
      if (contractorToEdit) {
        setTitle("contractor_editing");
      } else {
        setTitle("contractor_adding");
      }
    }
  }, [isDrawerOpen, contractorToEdit]);

  useEffect(() => {
    if (contractorToEdit) {
      form.setFieldsValue(contractorToEdit);
    } else {
      form.resetFields();
    }
  }, [contractorToEdit, form, isDrawerOpen]);

  const handleClose = useCallback(() => {
    dispatch(slice.actions.closeDrawer());
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  const handleFinish = useCallback(
    (model: EditContractorFormInputModel) => {
      if (contractorToEdit) {
        dispatch(slice.actions.updateContractor(ContractorDataConverter.convertContractorForUpdate({ ...model, id: contractorToEdit.id })));
      } else {
        dispatch(
          slice.actions.createContractor(ContractorDataConverter.convertContractorForUpdate({ ...model, companyId: userInfo.company.id })),
        );
      }
    },
    [dispatch, contractorToEdit, userInfo],
  );

  const handleReset = useCallback(() => {
    if (contractorToEdit) {
      form.setFieldsValue(contractorToEdit);
    } else {
      form.resetFields();
    }
  }, [form, contractorToEdit]);

  return (
    <Drawer
      onClose={handleClose}
      open={isDrawerOpen}
      title={t(title)}
      size={1000}
      extra={
        <Flex gap={8}>
          <Button onClick={handleReset}>{t("cancel")}</Button>
          <Button loading={isDrawerLoading} type="primary" onClick={handleSubmit}>
            {t("save")}
          </Button>
        </Flex>
      }
      destroyOnHidden
    >
      <Form
        initialValues={{ requisites: { template: "RU" }, contracts: [], type: "Юридическое лицо" }}
        autoComplete="off"
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        scrollToFirstError={{ behavior: "smooth", block: "end", focus: true }}
      >
        <Row gutter={8}>
          <Col span={24}>
            <Form.Item name="name" rules={[{ required: true, message: t("enter_name") }]} label={t("name")}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="type" noStyle>
              <Input hidden />
            </Form.Item>
          </Col>
          {/* <Col span={24}>
            <Form.Item name="type" rules={[{ required: true, message: t("enter_kind_of_contractor") }]} label={t("kind_of_contractor")}>
              <AutoComplete />
            </Form.Item>
          </Col> */}
          <Col span={12}>
            <Form.Item
              name="contractorTypes"
              rules={[{ required: true, message: t("choose_contractor_types") }]}
              label={t("contractor_types")}
            >
              <Select
                mode="multiple"
                options={[
                  { label: getContractorTypeLabel("CLIENT"), value: "CLIENT" },
                  { label: getContractorTypeLabel("CARRIER"), value: "CARRIER" },
                  { label: getContractorTypeLabel("SUPPLIER"), value: "SUPPLIER" },
                  { label: getContractorTypeLabel("OTHER"), value: "OTHER" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="countryId" rules={[{ required: true, message: t("choose_country") }]} label={t("country")}>
              <Select
                showSearch={{ filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
                options={countries.map((x) => ({ label: getLocalizedName(x, lang), value: x.id }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="isBlackList" valuePropName="checked">
              <Checkbox>{t("black_list")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider>{t("requisite")}</Divider>

        <Form.Item name={["requisites", "id"]} noStyle>
          <Input hidden />
        </Form.Item>

        <Form.Item name={["requisites", "template"]} label={t("template")}>
          <Select
            options={[
              { label: `${t("template")} "${getLocalizedName(russia, lang)}"`, value: "RU" },
              { label: `${t("template")} "${getLocalizedName(kazahstan, lang)}"`, value: "KZ" },
              { label: `${t("template")} "${getLocalizedName(belarus, lang)}"`, value: "BY" },
              { label: `${t("template")} "${getLocalizedName(china, lang)}"`, value: "CN" },
              { label: `${t("template")} "${t("other")}"`, value: "OTHER" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name={["requisites", "contractorName"]}
          rules={[{ required: true, message: t("enter_contractor_name") }]}
          label={t("contractor_name")}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={["requisites", "fullContractorName"]}
          rules={[{ required: true, message: t("enter_full_contractor_name") }]}
          label={t("full_contractor_name")}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={["requisites", "identificationNumber"]}
          rules={[{ required: true, message: t("enter_identification_number") }]}
          label={t("identification_number")}
        >
          <Input />
        </Form.Item>
        {template === "RU" && (
          <Form.Item name={["requisites", "kpp"]} label="КПП">
            <Input />
          </Form.Item>
        )}
        <Form.Item name={["requisites", "director"]} label={t("director")}>
          <Input />
        </Form.Item>

        <Divider>{t("contracts")}</Divider>

        <Form.List name="contracts">
          {(fields, { add, remove }) => (
            <>
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  style={{ width: "100%" }}
                  icon={<PlusOutlined />}
                >
                  {t("add_contract")}
                </Button>
              </Form.Item>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="middle">
                  <Col span={6}>
                    <Form.Item {...restField} name={[name, "contractNumber"]} label={t("contract_number")}>
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, "contractDate"]}
                      rules={[{ required: true, message: t("choose_contract_date") }]}
                      label={t("contract_date")}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[name, "currencyId"]}
                      label={t("currency")}
                      rules={[{ required: true, message: t("choose_currency") }]}
                    >
                      <Select options={currencies.map((x) => ({ label: x.name, value: x.id }))} />
                    </Form.Item>
                  </Col>

                  <Col span={5}>
                    <Form.Item
                      name={[name, "type"]}
                      rules={[{ required: true, message: t("choose_contract_type") }]}
                      label={t("contract_type")}
                    >
                      <Select
                        options={[
                          { label: `${t("logistics_contract")} (${t("client")})`, value: "CLIENT" },
                          { label: `${t("logistics_contract")} (${t("carrier")})`, value: "CARRIER" },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={1}>
                    <Form.Item label=" ">
                      <MinusCircleOutlined
                        onClick={() => {
                          remove(name);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Form.List>

        <Divider>{t("address")}</Divider>

        <Form.Item name={["address", "id"]} noStyle>
          <Input hidden />
        </Form.Item>
        <Form.Item name={["address", "address"]} label={t("address")}>
          <Input />
        </Form.Item>
        <Form.Item name={["address", "city"]} label={t("city")}>
          <Input />
        </Form.Item>
        <Form.Item name={["address", "postalCode"]} label={t("postal_code")}>
          <Input />
        </Form.Item>
        <Form.Item name={["address", "region"]} label={t("region")}>
          <Input />
        </Form.Item>
        <Form.Item name={["address", "province"]} label={t("province")}>
          <Input />
        </Form.Item>
        <Form.Item name={["address", "countryId"]} label={t("country")}>
          <Select
            showSearch={{ filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
            options={countries.map((x) => ({ label: getLocalizedName(x, lang), value: x.id }))}
          />
        </Form.Item>

        <Divider>{t("bank_requisites")}</Divider>

        <Form.List name="bankDetails">
          {(fields, { add, remove }) => (
            <>
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  style={{ width: "100%" }}
                  icon={<PlusOutlined />}
                >
                  {t("add_bank_requisite")}
                </Button>
              </Form.Item>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="middle">
                  <Col span={23}>
                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      rules={[{ required: true, message: t("enter_name") }]}
                      label={t("name")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Form.Item label=" ">
                      <MinusCircleOutlined
                        onClick={() => {
                          remove(name);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, "bankName"]}
                      rules={[{ required: true, message: t("enter_bank_name") }]}
                      label={t("bank_name")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[name, "bankAddress"]} label={t("bank_address")}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[name, "settlementAccount"]}
                      rules={[{ required: true, message: t("enter_settlement_account") }]}
                      label={t("settlement_account")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name={[name, "currencyId"]}
                      label={t("currency")}
                      rules={[{ required: true, message: t("choose_currency") }]}
                    >
                      <Select options={currencies.map((x) => ({ label: x.name, value: x.id }))} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name={[name, "swift"]} rules={[{ required: true, message: t("enter_swift") }]} label="SWIFT">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Form.List>

        <Divider>{t("contacts")}</Divider>

        <Form.List name="contacts">
          {(fields, { add, remove }) => (
            <>
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add({ type: "PHONE" });
                  }}
                  style={{ width: "100%" }}
                  icon={<PlusOutlined />}
                >
                  {t("add_contact")}
                </Button>
              </Form.Item>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="middle">
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      rules={[{ required: true, message: t("enter_name") }]}
                      label={t("name")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item {...restField} name={[name, "position"]} label={t("job_title")}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, "value"]}
                      rules={[{ required: true, message: t("enter_value") }]}
                      label={t("value")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item name={[name, "type"]} label={t("type")}>
                      <Select
                        options={[
                          { label: getContactTypeLabel("PHONE"), value: "PHONE" },
                          { label: getContactTypeLabel("EMAIL"), value: "EMAIL" },
                          { label: getContactTypeLabel("OTHER"), value: "OTHER" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Form.Item label=" ">
                      <MinusCircleOutlined
                        onClick={() => {
                          remove(name);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Form.List>

        {contractorToEdit && (
          <>
            <Divider />
            <Form.Item name="responsibleId" label={t("responsible")}>
              <Select
                showSearch={{ filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
                options={users.map((x) => ({ label: x.fullName, value: x.id }))}
              />
            </Form.Item>
          </>
        )}

        <Divider />
        <Form.Item name="comment" label={t("comment")}>
          <Input.TextArea autoSize />
        </Form.Item>
        <Form.Item name="companyId" noStyle>
          <Input hidden />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export default React.memo(EditContractorDrawer);
